import { HttpClient } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  Optional
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Sort } from "@angular/material/sort";
import { select, Store } from "@ngrx/store";
import { combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { ITableColumn } from "src/app/common/components/table/interfaces/table-column";
import { INotification } from "src/app/common/interfaces/dialogs/notification-settings";
import { FeatureFlag } from "src/app/core/enums/feature-flag";
import { CrmModule } from "src/app/core/enums/permissions";
import { DynamicSnackBar } from "src/app/core/material/snack-bar/snack-bar";
import { AnalyticsService } from "src/app/core/services/analytics/analytics.service";
import { FeatureFlagService } from "src/app/core/services/feature-flag.service";
import { RootState } from "src/app/ngrx/root-reducers";
import { selectRegionEnabledModules, selectUserRegions } from "src/app/ngrx/selectors/app.selectors";
import {
  hasNotificationSettingsPermissions,
  someRegionsHasPermissions
} from "src/app/ngrx/selectors/permissions.selectors";
import { TabConfig } from "src/app/shared/ui/tabs/tabs.component";
import { notificationSettingsAuvColumns } from "../../constants/notification-settings-auv.columns";
import { notificationSettingsEnergyDataColumns } from "../../constants/notification-settings-energy-data.columns";
import { notificationSettingsKWP } from "../../constants/notification-settings-kwp.columns";
import { notificationSettingsStreetLightingColumns } from "../../constants/notification-settings-lighting.columns";
import { notificationSettingsNewsColumns } from "../../constants/notification-settings-news.columns";
import { notificationSettingsTYPColumns } from "../../constants/notification-settings-typ.columns";
import { getNotificationSettingsColumns } from "../../constants/notification-settings.columns";
import { NotificationSettingsService } from "../../services/notification-settings.service";

export type Tab =
  | "incident-management"
  | "three-year-plan"
  | "accounting"
  | "street-lighting"
  | "kwp"
  | "news"
  | "grid-connection";

const ANALYTIC_KEYS = {
  "incident-management": "NOTIFICATION_SERVICE.OUTAGE",
  "three-year-plan": "NOTIFICATION_SERVICE.THREE_YEARS_PLAN",
  accounting: "NOTIFICATION_SERVICE.AUV",
  "street-lighting": "NOTIFICATION_SERVICE.STREET_LIGHTNING",
  kwp: "NOTIFICATION_SERVICE.KWP",
  news: "NOTIFICATION_SERVICE.EDIT.NEWS.NEWS_TITLE",
  "grid-connection": "NOTIFICATION_SERVICE.ENERGY_DATA"
};

@Component({
  selector: "app-notification-settings-dialog",
  templateUrl: "./notification-settings.component.html",
  styleUrls: ["./notification-settings.component.scss"],
  providers: [
    {
      provide: NotificationSettingsService,
      deps: [DynamicSnackBar, HttpClient, Store]
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationSettingsComponent implements OnInit {
  private readonly featureFlag = inject(FeatureFlagService);
  private readonly hasOMEditPermission = toSignal(
    this.store.select(someRegionsHasPermissions, {
      permissions: ["module-00005.subscriptions.email.edit", "module-00005.subscriptions.sms.edit"],
      method: "some"
    })
  );

  public tabs: Array<TabConfig<Tab>> = this.getInitialTabs();

  public activeTabName: Tab;
  public selectedRegions: Array<INotification> = [];
  public showEditForm: boolean;
  public multiRegion: boolean;
  public readonly columnsConfigs = computed<{ [key in Tab]: Array<ITableColumn> }>(() => ({
    "incident-management": getNotificationSettingsColumns(this.hasOMEditPermission()),
    "three-year-plan": notificationSettingsTYPColumns,
    accounting: notificationSettingsAuvColumns,
    "street-lighting": notificationSettingsStreetLightingColumns,
    kwp: notificationSettingsKWP,
    news: notificationSettingsNewsColumns,
    "grid-connection": notificationSettingsEnergyDataColumns
  }));
  public sort: Sort = {
    active: "municipality",
    direction: "asc"
  };

  private readonly destroyRef$ = inject(DestroyRef);

  constructor(
    protected readonly notificationSettingsService: NotificationSettingsService,
    private readonly store: Store<RootState>,
    private readonly analytics: AnalyticsService,
    private readonly dialogRef: MatDialogRef<NotificationSettingsComponent>,
    private readonly cdr: ChangeDetectorRef,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    private readonly data: { tab: Tab }
  ) {}

  public ngOnInit(): void {
    this.notificationSettingsService.setDefaultSort(this.sort);

    this.initializeTabWithPermissions();
    this.store
      .pipe(
        select(selectUserRegions),
        map((regions) => regions.length > 1),
        takeUntilDestroyed(this.destroyRef$)
      )
      .subscribe((multiRegion) => {
        this.multiRegion = multiRegion;
        this.dialogRef.updateSize(multiRegion ? "70rem" : "600px");
        this.cdr.detectChanges();
      });
  }

  public close(): void {
    this.dialogRef.close();
  }

  public onPanelTabChange(tab: Tab): void {
    this.setTab(tab);
    this.analytics.trackNotificationTabClick(ANALYTIC_KEYS[tab]);
  }

  private setTab(tab: Tab): void {
    this.activeTabName = tab;
    this.notificationSettingsService.setTabName(tab);
  }

  private initializeTabWithPermissions(): void {
    combineLatest([
      this.store.pipe(
        select(selectRegionEnabledModules),
        map((activeModules) => [...activeModules, "street-lighting", "kwp", "news", "grid-connection"])
      ),
      this.store.pipe(select(hasNotificationSettingsPermissions, [CrmModule.OutageMonitoring])),
      this.store.pipe(select(hasNotificationSettingsPermissions, [CrmModule.ThreeYearPlan])),
      this.store.pipe(select(hasNotificationSettingsPermissions, [CrmModule.Accounting])),
      this.store.pipe(select(hasNotificationSettingsPermissions, [CrmModule.StreetLighting])),
      this.store.pipe(select(hasNotificationSettingsPermissions, [CrmModule.KWP])),
      this.store.pipe(select(hasNotificationSettingsPermissions, [CrmModule.GridConnection]))
    ])
      .pipe(
        map(
          ([
            activeModules,
            hasOMPermissions,
            hasTYPPermissions,
            hasAccountingPermissions,
            hasStreetLightingPermissions,
            hasKWPPermissions,
            hasGridConnectionPermissions
          ]) => ({
            activeModules,
            "incident-management": hasOMPermissions,
            "three-year-plan": hasTYPPermissions,
            accounting: hasAccountingPermissions,
            "street-lighting": hasStreetLightingPermissions,
            kwp: hasKWPPermissions,
            news: true,
            "grid-connection": hasGridConnectionPermissions
          })
        ),
        takeUntilDestroyed(this.destroyRef$)
      )
      .subscribe((props) => {
        this.tabs = this.tabs.map((tab) => ({
          ...tab,
          disabled: !(props.activeModules.includes(tab.name) && props[tab.name])
        }));
        const tab = this.data?.tab ?? this.tabs.find((obj) => !obj.disabled).name;

        this.setTab(tab);
        this.cdr.detectChanges();
      });
  }

  private getInitialTabs(): Array<TabConfig<Tab>> {
    const tabs: Array<TabConfig<Tab>> = [
      { name: "incident-management", label: ANALYTIC_KEYS["incident-management"] },
      { name: "three-year-plan", label: ANALYTIC_KEYS["three-year-plan"] },
      { name: "accounting", label: ANALYTIC_KEYS.accounting },
      { name: "street-lighting", label: ANALYTIC_KEYS["street-lighting"] },
      { name: "kwp", label: ANALYTIC_KEYS.kwp },
      { name: "news", label: ANALYTIC_KEYS.news }
    ];

    if (this.featureFlag.isEnabled(FeatureFlag.GCNotificationSettings)) {
      tabs.push({ name: "grid-connection", label: ANALYTIC_KEYS["grid-connection"] });
    }

    return tabs;
  }
}
